<template>
  <ChartCard data-widget-id="gender-by-stage">
    <ChartHeader
      label="What is the gender distribution by stage?"
      widget-download-id="gender-by-stage"
      tooltip-key="demo-survey-insights-line"
    />
    <div class="chart-and-legend-container">
      <div class="chart-container">
        <LineChart id="dsr-gender-by-stage" :data="chartData" :options="chartOptions" />
      </div>
      <ChartLegend :legend-items="chartLegendItems" justify-content="flex-start" />
    </div>
    <ChartExpand :is-expanded="isExpanded" @toggle-stages="toggleStages" />
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartLegend from '@/components/insights/Chart/ChartLegend.vue';
import ChartExpand from '@/components/insights/Chart/ChartExpand.vue';
import LineChart from '@/components/insights/Chart/LineChart.vue';
import { computed, ref } from 'vue';
import { useDemographicSurveyReport } from '@/stores/insights/demographicSurveyReport';
import { storeToRefs } from 'pinia';
import { useColors } from '@/composables/colors';
import { initTooltip } from '@/components/insights/ProcessInsights/DemographicSurveyReport/charts/helpers/tooltip';
import { useTracking } from '@/stores/tracking/tracking';
import { injectUndefinedBreakdown } from '@/components/insights/ProcessInsights/DemographicSurveyReport/charts/helpers/injectBreakdown';

const { genderByStage } = storeToRefs(useDemographicSurveyReport());
const {
  male,
  female,
  declined,
  unknown,
} = genderByStage.value;

const {
  grey600,
  candidateDeclined,
  candidateMale,
  candidateFemale,
  white,
} = useColors();

const isExpanded = ref(false);

const overallTotals = computed(() => ({
  review: (male?.review || 0)
    + (female?.review || 0)
    + (declined?.review || 0)
    + (unknown?.review || 0),
  screen: (male?.screen || 0)
    + (female?.screen || 0)
    + (declined?.screen || 0)
    + (unknown?.screen || 0),
  assessment: (male?.assessment || 0)
    + (female?.assessment || 0)
    + (declined?.assessment || 0)
    + (unknown?.assessment || 0),
  offer: (male?.offer || 0)
    + (female?.offer || 0)
    + (declined?.offer || 0)
    + (unknown?.offer || 0),
  hired: (male?.hired || 0)
    + (female?.hired || 0)
    + (declined?.hired || 0)
    + (unknown?.hired || 0),
}));

const chartData = computed(() => {
  if (isExpanded.value) {
    const datasets = [
      {
        label: 'Female',
        data: [
          ((female?.review || 0) / overallTotals.value.review) * 100,
          ((female?.screen || 0) / overallTotals.value.screen) * 100,
          ((female?.assessment || 0) / overallTotals.value.assessment) * 100,
          ((female?.offer || 0) / overallTotals.value.offer) * 100,
          ((female?.hired || 0) / overallTotals.value.hired) * 100,
        ],
        backgroundColor: candidateFemale.value,
        borderColor: candidateFemale.value,
      },
      {
        label: 'Male',
        data: [
          ((male?.review || 0) / overallTotals.value.review) * 100,
          ((male?.screen || 0) / overallTotals.value.screen) * 100,
          ((male?.assessment || 0) / overallTotals.value.assessment) * 100,
          ((male?.offer || 0) / overallTotals.value.offer) * 100,
          ((male?.hired || 0) / overallTotals.value.hired) * 100,
        ],
        backgroundColor: candidateMale.value,
        borderColor: candidateMale.value,
      },
      {
        label: 'Undefined',
        data: [
          (((declined?.review || 0) + (unknown?.review || 0)) / overallTotals.value.review) * 100,
          (((declined?.screen || 0) + (unknown?.screen || 0)) / overallTotals.value.screen) * 100,
          (((declined?.assessment || 0)
              + (unknown?.assessment || 0)) / overallTotals.value.assessment) * 100,
          (((declined?.offer || 0) + (unknown?.offer || 0)) / overallTotals.value.offer) * 100,
          (((declined?.hired || 0) + (unknown?.hired || 0)) / overallTotals.value.hired) * 100,
        ],
        backgroundColor: candidateDeclined.value,
        borderColor: candidateDeclined.value,
      },
    ].filter((dataset) => dataset.data[0] > 0);

    return {
      labels: ['Application', 'Screening', 'Assessment', 'Offer', 'Hired'],
      datasets,
    };
  }

  const datasets = [
    {
      label: 'Female',
      data: [
        ((female?.review || 0) / overallTotals.value.review) * 100,
        ((female?.hired || 0) / overallTotals.value.hired) * 100,
      ],
      backgroundColor: candidateFemale.value,
      borderColor: candidateFemale.value,
    },
    {
      label: 'Male',
      data: [
        ((male?.review || 0) / overallTotals.value.review) * 100,
        ((male?.hired || 0) / overallTotals.value.hired) * 100,
      ],
      backgroundColor: candidateMale.value,
      borderColor: candidateMale.value,
    },
    {
      label: 'Undefined',
      data: [
        (((declined?.review || 0) + (unknown?.review || 0)) / overallTotals.value.review) * 100,
        (((declined?.hired || 0) + (unknown?.hired || 0)) / overallTotals.value.hired) * 100,
      ],
      backgroundColor: candidateDeclined.value,
      borderColor: candidateDeclined.value,
    },
  ].filter((dataset) => dataset.data[0] > 0);

  return {
    labels: ['Application', 'Hired'],
    datasets,
  };
});

const chartLegendItems = computed(() => chartData.value.datasets.map((dataset) => ({
  label: dataset.label,
  color: dataset.backgroundColor,
})));

const suggestedMax = computed(() => {
  let max = 0;

  const hireMax = Math.max(
    ((male?.hired || 0) / overallTotals.value.hired) * 100,
    ((female?.hired || 0) / overallTotals.value.hired) * 100,
    (((declined?.hired || 0) + (unknown?.hired || 0)) / overallTotals.value.hired) * 100,
  );

  const applicationMax = Math.max(
    ((male?.review || 0) / overallTotals.value.review) * 100,
    ((female?.review || 0) / overallTotals.value.review) * 100,
    (((declined?.review || 0) + (unknown?.review || 0)) / overallTotals.value.review) * 100,
  );

  const screenMax = Math.max(
    ((male?.screen || 0) / overallTotals.value.screen) * 100,
    ((female?.screen || 0) / overallTotals.value.screen) * 100,
    (((declined?.screen || 0) + (unknown?.screen || 0)) / overallTotals.value.screen) * 100,
  );

  const assessmentMax = Math.max(
    ((male?.assessment || 0) / overallTotals.value.assessment) * 100,
    ((female?.assessment || 0) / overallTotals.value.assessment) * 100,
    (((declined?.assessment || 0)
      + (unknown?.assessment || 0))
      / overallTotals.value.assessment) * 100,
  );

  const offerMax = Math.max(
    ((male?.offer || 0) / overallTotals.value.offer) * 100,
    ((female?.offer || 0) / overallTotals.value.offer) * 100,
    (((declined?.offer || 0) + (unknown?.offer || 0)) / overallTotals.value.offer) * 100,
  );

  if (isExpanded.value) {
    max = Math.max(
      hireMax,
      applicationMax,
      screenMax,
      assessmentMax,
      offerMax,
    );
  } else {
    max = Math.max(hireMax, applicationMax);
  }

  return Math.ceil(max / 10) * 10;
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  datasets: {
    line: {
      borderWidth: 1,
    },
    point: {
      borderWidth: 1,
    },
  },
  scales: {
    y: {
      position: 'left',
      beginAtZero: true,
      suggestedMax: suggestedMax.value,
      min: 0,
      border: {
        display: true,
        dash: [5, 5],
      },
      grid: {
        display: true,
        tickWidth: 0,
      },
      ticks: {
        callback: (value) => `${value}%`,
        color: grey600.value,
        font: {
          size: 10,
        },
        maxTicksLimit: suggestedMax.value / 10 + 1,
      },
    },
    y1: {
      position: 'right',
      beginAtZero: true,
      suggestedMax: suggestedMax.value,
      min: 0,
      grid: {
        display: false,
      },
      ticks: {
        display: false,
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: grey600.value,
        font: {
          size: 13,
          weight: 400,
        },
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    tooltip: {
      enabled: false,
      position: 'nearest',
      external: (context) => initTooltip({
        context,
        tooltipId: 'gender-by-stage-tooltip',
        createLineItems: (tooltip) => {
          const { dataIndex } = tooltip?.dataPoints?.[0] || {};
          // eslint-disable-next-line no-underscore-dangle
          const { datasets } = tooltip?.chart?.config?._config?.data || {};

          if (dataIndex === undefined || !datasets) {
            return [];
          }

          const sortedDatasets = datasets
            .map((dataset) => {
              const data = dataset.data[dataIndex];
              const { label } = dataset;
              const value = `${data.toFixed(1)}%`;
              const color = dataset.backgroundColor;
              const isBold = dataset.label === tooltip.dataPoints[0].dataset.label;

              return {
                value: `${value} ${label}`,
                color,
                isBold,
                data,
              };
            }).sort((a, b) => b.data - a.data);

          const { label } = tooltip.dataPoints[0];

          const finalDatasets = injectUndefinedBreakdown(
            sortedDatasets,
            label,
            genderByStage,
            overallTotals,
            white,
          );

          return finalDatasets;
        },
        createTitle: (tooltip) => tooltip.dataPoints[0].label,
      }),
    },
  },
}));

const toggleStages = () => {
  if (!isExpanded.value) {
    useTracking().trackName({
      name: 'Expanded Gender Graph on Demographic-survey-report page',
    });
  }

  isExpanded.value = !isExpanded.value;
};
</script>

<style lang="less" scoped>
.chart-and-legend-container {
  padding: 10px 16px 5px 10px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 80px;
  gap: 20px;
}

.chart-container {
  height: 280px;
  width: 100%;
}
</style>

<style lang="less">
.gender-by-stage-toggle-tooltip {
  font-size: 10px !important;
  font-weight: 500 !important;
  line-height: 12px !important;
  padding: 8px !important;
}
</style>
